import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  Leaf,
  Shield,
  AlertTriangle,
  FileText,
  Building,
  CheckCircle,
  Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const ProjectDetail = () => {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error("Error fetching project data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, token]);

  const formatCurrency = (val) => {
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading project details...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-slate-400 text-xl">Project not found</p>
        <Button onClick={() => navigate('/')} className="mt-4 btn-secondary">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const project = data;
  const deliverables = data.deliverables || [];
  const risks = data.risks || [];
  const budgets = data.budgets || [];
  const expenses = data.expenses || [];
  const disbursements = data.disbursements || [];
  const esg_scores = data.esg_scores || { environmental: 85, social: 78, governance: 82 };
  const compliance = data.compliance || [];
  const audit_logs = data.audit_logs || [];
  const vendors = data.vendors || [];

  const roiChartOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
    xAxis: { type: 'category', data: ['Q1', 'Q2', 'Q3', 'Q4'], axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8' } },
    yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: '#1e293b' } }, axisLabel: { color: '#94a3b8', formatter: '{value}%' } },
    series: [{ data: [12, 15, 14, project.completion_percentage || 0], type: 'line', smooth: true, lineStyle: { color: '#22c55e', width: 3 }, areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(34, 197, 94, 0.3)' }, { offset: 1, color: 'rgba(34, 197, 94, 0)' }] } }, itemStyle: { color: '#22c55e' } }]
  };

  const burnRateOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 162, 39, 0.3)', textStyle: { color: '#f8fafc' } },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
    xAxis: { type: 'category', data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'], axisLine: { lineStyle: { color: '#334155' } }, axisLabel: { color: '#94a3b8' } },
    yAxis: { type: 'value', axisLine: { show: false }, splitLine: { lineStyle: { color: '#1e293b' } }, axisLabel: { color: '#94a3b8' } },
    series: [{ data: [8, 12, 15, 18, 22, 25], type: 'bar', barWidth: '60%', itemStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: '#c9a227' }, { offset: 1, color: 'rgba(201, 162, 39, 0.3)' }] }, borderRadius: [4, 4, 0, 0] } }]
  };

  return (
    <div className="space-y-6" data-testid="project-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {project.name}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-slate-400">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {project.country}, {project.province}</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {formatDate(project.start_date)} - {formatDate(project.end_date)}</span>
          </div>
        </div>
        <Badge className={project.status === 'active' ? 'status-green text-lg px-4 py-2' : 'status-yellow text-lg px-4 py-2'}>
          {project.status}
        </Badge>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <DollarSign className="w-8 h-8 text-[#c9a227] mx-auto" />
            <p className="text-2xl font-bold text-[#c9a227] mt-2">{formatCurrency(project.budget_total)}</p>
            <p className="text-xs text-slate-500">Budget</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-8 h-8 text-green-400 mx-auto" />
            <p className="text-2xl font-bold text-green-400 mt-2">{project.completion_percentage || 0}%</p>
            <p className="text-xs text-slate-500">Completion</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-blue-400 mx-auto" />
            <p className="text-2xl font-bold text-blue-400 mt-2">Stage {project.stage_gate || 1}</p>
            <p className="text-xs text-slate-500">Stage Gate</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <Leaf className="w-8 h-8 text-emerald-400 mx-auto" />
            <p className="text-2xl font-bold text-emerald-400 mt-2">85</p>
            <p className="text-xs text-slate-500">ESG Score</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <Shield className="w-8 h-8 text-purple-400 mx-auto" />
            <p className="text-2xl font-bold text-purple-400 mt-2">{project.approval_status === 'approved' ? '100' : '75'}%</p>
            <p className="text-xs text-slate-500">Compliance</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 text-cyan-400 mx-auto" />
            <p className="text-2xl font-bold text-cyan-400 mt-2">{Math.floor((project.budget_total || 0) / 100000).toLocaleString()}</p>
            <p className="text-xs text-slate-500">Jobs</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-900/50 border border-slate-700 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Overview</TabsTrigger>
          <TabsTrigger value="financials" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Financials</TabsTrigger>
          <TabsTrigger value="trade" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Trade & Liquidity</TabsTrigger>
          <TabsTrigger value="governance" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Governance</TabsTrigger>
          <TabsTrigger value="risks" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Risks</TabsTrigger>
          <TabsTrigger value="vendors" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Vendors</TabsTrigger>
          <TabsTrigger value="deliverables" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Deliverables</TabsTrigger>
          <TabsTrigger value="esg" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">ESG Impact</TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-[#c9a227]/20 data-[state=active]:text-[#c9a227]">Audit Trail</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="glass-card lg:col-span-2">
              <CardHeader><CardTitle className="text-slate-200">Project Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300">{project.description}</p>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700">
                  <div><p className="text-slate-500 text-sm">Region</p><p className="text-white">{project.region}</p></div>
                  <div><p className="text-slate-500 text-sm">Sector</p><p className="text-white">{project.sector}</p></div>
                  <div><p className="text-slate-500 text-sm">Project Owner</p><p className="text-white">{project.project_owner}</p></div>
                  <div><p className="text-slate-500 text-sm">Project Manager</p><p className="text-white">{project.project_manager}</p></div>
                  <div><p className="text-slate-500 text-sm">Stage Gate</p><p className="text-[#c9a227] font-semibold">Stage {project.stage_gate}</p></div>
                  <div><p className="text-slate-500 text-sm">Coordinates</p><p className="text-white">{project.coordinates?.lat?.toFixed(4)}, {project.coordinates?.lng?.toFixed(4)}</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardHeader><CardTitle className="text-slate-200">Stage Gate Progress</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1,2,3,4,5].map(stage => (
                    <div key={stage} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stage <= project.stage_gate ? 'bg-[#c9a227]' : 'bg-slate-700'}`}>
                        {stage <= project.stage_gate ? <CheckCircle className="w-5 h-5 text-[#0b1c2d]" /> : <span className="text-slate-400">{stage}</span>}
                      </div>
                      <span className={stage <= project.stage_gate ? 'text-white' : 'text-slate-500'}>Stage {stage}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financials Tab */}
        <TabsContent value="financials" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="glass-card">
              <CardHeader><CardTitle className="text-slate-200">ROI Trend</CardTitle></CardHeader>
              <CardContent><ReactECharts option={roiChartOptions} style={{ height: '300px' }} /></CardContent>
            </Card>
            <Card className="glass-card">
              <CardHeader><CardTitle className="text-slate-200">Expense Burn Rate</CardTitle></CardHeader>
              <CardContent><ReactECharts option={burnRateOptions} style={{ height: '300px' }} /></CardContent>
            </Card>
          </div>
          <Card className="glass-card mt-6">
            <CardHeader><CardTitle className="text-slate-200">Budget Breakdown</CardTitle></CardHeader>
            <CardContent>
              <table className="data-table">
                <thead><tr><th>Category</th><th>Allocated</th><th>Spent</th><th>Remaining</th><th>Status</th></tr></thead>
                <tbody>
                  {budgets?.map((b, idx) => (
                    <tr key={idx}>
                      <td className="text-white">{b.category}</td>
                      <td className="text-[#c9a227]">{formatCurrency(b.allocated_amount)}</td>
                      <td className="text-red-400">{formatCurrency(b.spent_amount)}</td>
                      <td className="text-green-400">{formatCurrency(b.allocated_amount - b.spent_amount)}</td>
                      <td><Badge className={(b.spent_amount / b.allocated_amount) < 0.8 ? 'status-green' : 'status-yellow'}>
                        {Math.round((b.spent_amount / b.allocated_amount) * 100)}%
                      </Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trade & Liquidity Tab */}
        <TabsContent value="trade" className="mt-6">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-slate-200">Disbursements</CardTitle></CardHeader>
            <CardContent>
              {disbursements?.length > 0 ? (
                <table className="data-table">
                  <thead><tr><th>Purpose</th><th>Amount</th><th>Date</th><th>Status</th></tr></thead>
                  <tbody>
                    {disbursements.map((d, idx) => (
                      <tr key={idx}>
                        <td className="text-white">{d.purpose}</td>
                        <td className="text-[#c9a227]">{formatCurrency(d.amount)}</td>
                        <td className="text-slate-400">{formatDate(d.disbursement_date)}</td>
                        <td><Badge className="status-green">{d.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-slate-400 text-center py-8">No disbursements recorded</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Governance Tab */}
        <TabsContent value="governance" className="mt-6">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-slate-200">Compliance Controls</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {compliance?.map((c, idx) => (
                  <div key={idx} className="p-4 bg-slate-800/30 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#c9a227] font-semibold">{c.framework}</span>
                      <Badge className={c.status === 'compliant' ? 'status-green' : c.status === 'partial' ? 'status-yellow' : 'status-red'}>
                        {c.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400">{c.control_name}</p>
                    <p className="text-xs text-slate-500 mt-2">Next Review: {formatDate(c.next_review)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks" className="mt-6">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-slate-200">Risk Register</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {risks?.map((r, idx) => (
                  <div key={idx} className="p-4 bg-slate-800/30 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-5 h-5 ${r.risk_score > 3 ? 'text-red-400' : r.risk_score > 2 ? 'text-yellow-400' : 'text-green-400'}`} />
                        <span className="text-white font-medium">{r.name}</span>
                      </div>
                      <Badge className={r.risk_score > 3 ? 'status-red' : r.risk_score > 2 ? 'status-yellow' : 'status-green'}>
                        Score: {r.risk_score}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-2">{r.description}</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div><p className="text-slate-500">Category</p><p className="text-white">{r.category}</p></div>
                      <div><p className="text-slate-500">Probability</p><p className="text-white">{(r.probability * 100).toFixed(0)}%</p></div>
                      <div><p className="text-slate-500">Impact</p><p className="text-white">{(r.impact * 100).toFixed(0)}%</p></div>
                    </div>
                    <p className="text-sm text-slate-400 mt-2"><strong>Mitigation:</strong> {r.mitigation_strategy}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="mt-6">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-slate-200">Project Vendors</CardTitle></CardHeader>
            <CardContent>
              {vendors?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {vendors.map((v, idx) => (
                    <div key={idx} className="p-4 bg-slate-800/30 rounded-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Building className="w-5 h-5 text-[#c9a227]" />
                          <span className="text-white font-medium">{v.name}</span>
                        </div>
                        <Badge className={v.risk_rating === 'low' ? 'status-green' : 'status-yellow'}>
                          {v.risk_rating} risk
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-400">{v.country} • {v.services?.join(', ')}</p>
                      <div className="mt-2">
                        <p className="text-slate-500 text-sm">Performance Score</p>
                        <Progress value={v.performance_score} className="h-2 mt-1 bg-slate-700" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-400 text-center py-8">No vendors assigned</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deliverables Tab */}
        <TabsContent value="deliverables" className="mt-6">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-slate-200">Project Deliverables</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {deliverables?.map((d, idx) => (
                  <div key={idx} className="p-4 bg-slate-800/30 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {d.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Clock className="w-5 h-5 text-yellow-400" />}
                        <span className="text-white font-medium">{d.name}</span>
                      </div>
                      <Badge className={d.status === 'completed' ? 'status-green' : d.status === 'in_progress' ? 'status-yellow' : 'bg-slate-600'}>
                        {d.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400">{d.description}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-500">Due: {formatDate(d.due_date)}</span>
                      <div className="flex items-center gap-2">
                        <Progress value={d.completion_percentage} className="h-2 w-24 bg-slate-700" />
                        <span className="text-xs text-slate-400">{d.completion_percentage}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ESG Impact Tab */}
        <TabsContent value="esg" className="mt-6">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-slate-200">ESG Assessment</CardTitle></CardHeader>
            <CardContent>
              {esg_scores?.[0] ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-green-500/10 rounded-sm border border-green-500/20">
                    <Leaf className="w-12 h-12 text-green-400 mx-auto" />
                    <p className="text-4xl font-bold text-green-400 mt-4">{esg_scores[0].environmental_score}</p>
                    <p className="text-slate-400 mt-2">Environmental</p>
                  </div>
                  <div className="text-center p-6 bg-blue-500/10 rounded-sm border border-blue-500/20">
                    <Users className="w-12 h-12 text-blue-400 mx-auto" />
                    <p className="text-4xl font-bold text-blue-400 mt-4">{esg_scores[0].social_score}</p>
                    <p className="text-slate-400 mt-2">Social</p>
                  </div>
                  <div className="text-center p-6 bg-purple-500/10 rounded-sm border border-purple-500/20">
                    <Shield className="w-12 h-12 text-purple-400 mx-auto" />
                    <p className="text-4xl font-bold text-purple-400 mt-4">{esg_scores[0].governance_score}</p>
                    <p className="text-slate-400 mt-2">Governance</p>
                  </div>
                </div>
              ) : <p className="text-slate-400 text-center py-8">No ESG assessment available</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Trail Tab */}
        <TabsContent value="audit" className="mt-6">
          <Card className="glass-card">
            <CardHeader><CardTitle className="text-slate-200">Audit Trail</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {audit_logs?.length > 0 ? audit_logs.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-sm">
                    <FileText className="w-5 h-5 text-[#c9a227] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium capitalize">{log.action} - {log.entity_type}</p>
                        <span className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-400">By: {log.user_role}</p>
                      <p className="text-xs text-slate-500 mt-1 font-mono">Hash: {log.immutable_hash?.slice(0, 32)}...</p>
                    </div>
                  </div>
                )) : <p className="text-slate-400 text-center py-8">No audit logs available</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectDetail;
